:host {
  /* Color slots — overridden per [variant] below (info default). */
  --_bg: var(--notification-info-background);
  --_text: var(--notification-info-text);
  --_icon: var(--notification-info-icon);
  --_close-icon: var(--notification-info-close-icon);
  --_close-bg-hover: var(--notification-close-background-hover-on-color);

  display: flex;
  align-items: flex-start;
  gap: var(--notification-container-gap);
  inline-size: var(--notification-container-width);
  max-inline-size: 100%;
  box-sizing: border-box;
  padding-block-start: var(--notification-container-padding-top);
  padding-block-end: var(--notification-container-padding-bottom);
  padding-inline: var(--notification-container-padding-inline);
  border-radius: var(--notification-container-border-radius);
  overflow: clip;

  background-color: var(--_bg);
  color: var(--_text);

  font-family: var(--notification-body-font-family);
  font-size: var(--notification-body-font-size);
  line-height: var(--notification-body-line-height);
  font-weight: var(--notification-body-font-weight);

  /* Appearance: slide down from slightly above + fade in (Figma Behavior panel).
     Plays once on mount; the dismiss/exit transition stays consumer-owned since
     the atom cannot defer its own DOM removal. Disabled under reduced-motion. */
  animation: notification-appear var(--notification-container-appearance-duration)
    var(--notification-container-transition-timing-function) both;
}

@keyframes notification-appear {
  from {
    opacity: 0;
    transform: translateY(calc(-1 * var(--spacing-8)));
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ---------- variant matrix (filled) ---------- */

:host([variant='info']) {
  --_bg: var(--notification-info-background);
  --_text: var(--notification-info-text);
  --_icon: var(--notification-info-icon);
  --_close-icon: var(--notification-info-close-icon);
  --_close-bg-hover: var(--notification-close-background-hover-on-color);
}

:host([variant='warning']) {
  --_bg: var(--notification-warning-background);
  --_text: var(--notification-warning-text);
  --_icon: var(--notification-warning-icon);
  --_close-icon: var(--notification-warning-close-icon);
  --_close-bg-hover: var(--notification-close-background-hover);
}

:host([variant='success']) {
  --_bg: var(--notification-success-background);
  --_text: var(--notification-success-text);
  --_icon: var(--notification-success-icon);
  --_close-icon: var(--notification-success-close-icon);
  --_close-bg-hover: var(--notification-close-background-hover-on-color);
}

:host([variant='error']) {
  --_bg: var(--notification-error-background);
  --_text: var(--notification-error-text);
  --_icon: var(--notification-error-icon);
  --_close-icon: var(--notification-error-close-icon);
  --_close-bg-hover: var(--notification-close-background-hover-on-color);
}

/* ---------- main (leading icon + content) ---------- */

.main {
  flex: 1 1 auto;
  min-inline-size: 0;
  display: flex;
  align-items: flex-start;
  gap: var(--notification-container-content-gap);
}

/* ---------- leading icon ---------- */

.icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--notification-icon-padding);
  color: var(--_icon);
}

.icon mud-icon {
  --icon-color: currentcolor;
}

::slotted([slot='icon-start']) {
  display: inline-flex;
  inline-size: var(--notification-icon-size);
  block-size: var(--notification-icon-size);
  color: var(--_icon);
}

::slotted(mud-icon[slot='icon-start']) {
  --icon-color: currentcolor;
}

/* ---------- content stack ---------- */

.content {
  flex: 1 1 auto;
  min-inline-size: 0;
  display: flex;
  flex-direction: column;
  gap: var(--notification-container-stack-gap);
}

.heading {
  display: flex;
  flex-direction: column;
  gap: var(--notification-container-heading-gap);
}

.title {
  margin: 0;
  font-family: var(--notification-title-font-family);
  font-size: var(--notification-title-font-size);
  line-height: var(--notification-title-line-height);
  font-weight: var(--notification-title-font-weight);
  letter-spacing: var(--notification-title-letter-spacing);
  color: inherit;
  /* Heading stays readable in full but clamps to 2 lines with an ellipsis for
     overly-long titles ("minimal truncation" per the Figma Truncation panel).
     A bare `text-overflow: ellipsis` does nothing without `nowrap`/line-clamp. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.body {
  margin: 0;
  font-family: var(--notification-body-font-family);
  font-size: var(--notification-body-font-size);
  line-height: var(--notification-body-line-height);
  font-weight: var(--notification-body-font-weight);
  color: inherit;
  /* Body wraps naturally; overflow-wrap prevents overflow from very long tokens. */
  overflow-wrap: anywhere;
}

/* ---------- actions (inline link / button group) ---------- */

.actions {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-8);
}

:host(:not(.has-actions)) .actions {
  display: none;
}

::slotted([slot='actions']) {
  color: inherit;
}
::slotted(mud-button[slot='actions']) {
  --_label: currentColor;
  --_label-hover: currentColor;
  --_label-active: currentColor;
  --_icon: currentColor;
  --_border-color: currentColor;
  --_border-color-hover: currentColor;
  --_border-color-active: currentColor;
  --_bg-hover: color-mix(in srgb, currentColor 16%, transparent);
  --_bg-active: color-mix(in srgb, currentColor 28%, transparent);
}

/* ---------- close button ---------- */

.close {
  flex-shrink: 0;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Target size per the Figma Target Sizes panel: 32px on pointer devices,
     40px on touch (below). The × glyph stays 16px; only the hit area grows. */
  inline-size: var(--notification-close-size-pointer);
  block-size: var(--notification-close-size-pointer);
  padding: 0;

  border: 0;
  border-radius: var(--notification-close-border-radius);
  background-color: transparent;
  color: var(--_close-icon);

  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  transition:
    background-color var(--notification-container-transition-duration)
      var(--notification-container-transition-timing-function),
    color var(--notification-container-transition-duration) var(--notification-container-transition-timing-function);
}

/* Touch devices: grow the hit target to 40px (Figma Target Sizes panel). */
@media (pointer: coarse) {
  .close {
    inline-size: var(--notification-close-size-touch);
    block-size: var(--notification-close-size-touch);
  }
}

.close:hover {
  background-color: var(--_close-bg-hover);
}

.close:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 var(--focus-ring-width-inner) var(--focus-ring-color-inner),
    0 0 0 calc(var(--focus-ring-width-inner) + var(--focus-ring-width-outer)) var(--focus-ring-color-outer);
  position: relative;
  z-index: 1;
}

.close-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--notification-close-icon-size);
  block-size: var(--notification-close-icon-size);
}

.close-icon svg {
  display: block;
  inline-size: 100%;
  block-size: 100%;
  pointer-events: none;
}

/* ---------- reduced motion ---------- */

@media (prefers-reduced-motion: reduce) {
  :host {
    animation: none;
  }

  .close {
    transition: none;
  }
}
